import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { RequestCard } from '../components/RequestCard';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { DonationRequest } from '../types';
import { useAuth } from '../hooks/useAuth';

interface AnonymousDonation {
  amount: number;
  date: Date;
  isAnonymous: true;
}

interface UserDonation {
  userId: string;
  amount: number;
  date: Date;
  isAnonymous: false;
}

type Donation = AnonymousDonation | UserDonation;

interface ExtendedDonationRequest extends DonationRequest {
  minDonationAmount: number;
  userId: string;
  creatorName: string;
  creatorProfilePicture?: string;
  creatorCity: string;
  creatorCountry: string;
  donations: Donation[];
  uniqueDonorsCount: number;
  location: {
    city: string;
    country: string;
  };
  hasFixedAmount: boolean;
  createdAt: Date;
}

export function RequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [request, setRequest] = useState<ExtendedDonationRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileSettings] = useState(() => {
    const saved = localStorage.getItem('profile_settings');
    return saved ? JSON.parse(saved) : null;
  });
  const { user } = useAuth();

  const fetchRequest = async () => {
    setIsLoading(true);
    try {
      // Vérifier que l'ID est défini
      if (!id) {
        console.error("ID de la demande non défini");
        setIsLoading(false);
        return;
      }
      
      // Logs pour comprendre l'authentification
      console.log("Utilisateur actuel (useAuth):", user);
      console.log("ID utilisateur (useAuth):", user?.id);
      
      const requestDoc = await getDoc(doc(db, '2dr_requests', id));
      if (requestDoc.exists()) {
        const data = requestDoc.data();
        
        console.log("Données de la demande:", data);
        
        // Vérifier si la demande est privée
        if (!data.isPublic) {
          console.log("ID propriétaire:", data.userId);
          
          // Vérifier si c'est un lien partagé
          const isSharedLink = window.location.pathname.includes('/shared/');
          
          // Vérifier si l'utilisateur est propriétaire
          const isOwner = user && user.id === data.userId;
          
          console.log("Est propriétaire:", isOwner);
          console.log("Est lien partagé:", isSharedLink);
          
          // Si ce n'est ni le propriétaire ni un lien partagé, rediriger
          if (!isOwner && !isSharedLink) {
            console.log("Accès refusé - redirection vers 2DR");
            navigate('/2dr');
            return;
          }
        }
        
        // Si l'accès est autorisé, continuer avec le traitement des données
        const formattedDonations = (data.donations || []).map((d: any) => ({
          ...d,
          date: d.date ? new Date(d.date) : new Date(),
          isAnonymous: !!d.isAnonymous
        }));

        const request: ExtendedDonationRequest = {
          id: requestDoc.id,
          title: data.title || '',
          details: data.details || '',
          amount: data.amount || 0,
          city: data.city || '',
          country: data.country || '',
          images: data.images || [],
          audioUrl: data.audioUrl || null,
          isPublic: data.isPublic ?? true,
          link: data.link || '',
          likes: data.likes || 0,
          minDonationAmount: data.minDonationAmount || 100,
          userId: data.userId || 'unknown',
          creatorName: data.creatorName || 'Anonyme',
          creatorCity: data.city || 'Non spécifié',
          creatorCountry: data.country || 'Non spécifié',
          creatorProfilePicture: data.creatorProfilePicture,
          donations: formattedDonations,
          uniqueDonorsCount: formattedDonations.filter((d: any) => !d.isAnonymous).length,
          location: {
            city: data.city || '',
            country: data.country || ''
          },
          hasFixedAmount: data.hasFixedAmount || false,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date()
        };
        setRequest(request);
        console.log("Données de la demande:", request);
        console.log("Utilisateur actuel:", user);
        console.log("ID utilisateur:", user?.id);
        console.log("ID propriétaire:", request.userId);
        console.log("Est public:", request.isPublic);

        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setIsLoading(false);
    }

    // Si on n'a pas trouvé la demande dans Firestore mais qu'on a une version locale
    if (request) {
      return;
    }

    // Si on n'a pas trouvé la demande ni dans Firestore ni en local
    setIsLoading(false);
  };

  useEffect(() => {
    fetchRequest();
  }, [id, navigate, location.state, user]);

  const handleLike = () => {
    if (!request) return;
    
    const savedRequests = localStorage.getItem('2dr_requests');
    if (savedRequests) {
      const requests: DonationRequest[] = JSON.parse(savedRequests);
      const updatedRequests = requests.map(r => {
        if (r.id === request.id) {
          const isLiked = localStorage.getItem(`liked_${request.id}`) === 'true';
          return {
            ...r,
            likes: r.likes + (isLiked ? -1 : 1)
          };
        }
        return r;
      });
      localStorage.setItem('2dr_requests', JSON.stringify(updatedRequests));
      setRequest(prev => prev ? {
        ...prev,
        likes: prev.likes + (localStorage.getItem(`liked_${prev.id}`) === 'true' ? -1 : 1)
      } : null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-400">Chargement de la publication...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Publication introuvable</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="bg-black/50 text-white p-4 flex items-center justify-between sticky top-0 z-10 backdrop-blur-lg border-b border-white/10">
        <button
          onClick={() => navigate('/2dr')}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Détails de publication</h1>
        <div className="w-6" /> {/* Spacer for alignment */}
      </div>

      <div className="container mx-auto px-4 py-4 max-w-2xl">
        <RequestCard
          {...request}
          creatorId={request.userId}
          name={profileSettings?.username || 'Anonyme'}
          profilePicture={profileSettings?.profilePicture}
          description={request.details}
          goal={request.amount}
          minDonationAmount={request.minDonationAmount || 100}
          hasDonated={request.donations.some(d => 
            !d.isAnonymous && d.userId === 'user123'
          )}
          onLike={handleLike}
          collected={request.donations.reduce((sum, d) => sum + d.amount, 0)}
          creatorName={request.creatorName}
          creatorCity={request.creatorCity}
          creatorCountry={request.creatorCountry}
        />
      </div>
    </div>
  );
}